import React from 'react';

function lazyRetry(importFn) {
  const RELOAD_KEY = 'chunkReloadAttempted';
  const clearFlag = (mod) => { sessionStorage.removeItem(RELOAD_KEY); return mod; };
  return React.lazy(() =>
    importFn()
      .then(clearFlag)
      .catch(() =>
        new Promise(resolve => setTimeout(resolve, 1000))
          .then(() => importFn())
          .then(clearFlag)
          .catch((err) => {
            // Stale chunk after a container hot-swap: index.html now references
            // new hashed filenames the open tab doesn't know about. One hard
            // reload picks them up. sessionStorage flag prevents reload loops.
            if (!sessionStorage.getItem(RELOAD_KEY)) {
              sessionStorage.setItem(RELOAD_KEY, '1');
              window.location.reload();
              return new Promise(() => {});
            }
            throw err;
          })
      )
  );
}

const Home = lazyRetry(() => import( '../Home'));
const SopList = lazyRetry(() => import('../Documents/SopTemplates'));
const Sop = lazyRetry(() => import( '../Documents/Sop'));
const FormTemplates = lazyRetry(() => import('../Documents/FormTemplates'));
const FormRecord = lazyRetry(() => import('../Documents/FormRecord'));
const UserList = lazyRetry(() => import('../Config/UserList'));
const User = lazyRetry(() => import('../Config/User'));
const DocumentCertification = lazyRetry(() => import('../Documents/DocumentCertification'));
const AdminSettings = lazyRetry(() => import( '../Admin/AdminSettings'));
const Deviation = lazyRetry(() => import( '../Investigations/Deviation'));
const DeviationList = lazyRetry(() => import( '../Investigations/Deviations'));
const NonConformance = lazyRetry(() => import( '../Investigations/Ncr'));
const NcrList = lazyRetry(() => import( '../Investigations/Ncrs'));
const ChangePassword = lazyRetry(() => import( '../Admin/ChangePassword'));
const FormComponent = lazyRetry(() => import( '../Documents/FormComponent'));
const DestructionList = lazyRetry(() => import( '../Facility/Destructions'));
const Destruction = lazyRetry(() => import( '../Facility/Destruction'));
const ValueTypes = lazyRetry(() => import( '../Config/ValueTypes'));
const Values = lazyRetry(() => import( '../Config/Values'));
const IncidentList = lazyRetry(() => import( '../Investigations/Incidents'));
const Incident = lazyRetry(() => import( '../Investigations/Incident'));
const EmployeeIllnessList = lazyRetry(() => import( '../Investigations/EmployeeIllnessList'));
const EmployeeIllness = lazyRetry(() => import( '../Investigations/EmployeeIllness'));
const LossCannabisList = lazyRetry(() => import( '../Investigations/LossCannabisList'));
const LossCannabis = lazyRetry(() => import( '../Investigations/LossCannabis'));
const PesticidePreparationList = lazyRetry(() => import( '../Cultivation/PesticidePreparations'));
const PesticidePreparation = lazyRetry(() => import( '../Cultivation/PesticidePreparation'));
const VistorList = lazyRetry(() => import( '../Facility/VisitorList'));
const Visitor = lazyRetry(() => import( '../Facility/Visitor'));
const SigninList = lazyRetry(() => import( '../Facility/SigninList'));
const Signin = lazyRetry(() => import( '../Facility/Signin'));
const SigninKiosk = lazyRetry(() => import( '../Facility/SigninKiosk'));
const KioskEntry = lazyRetry(() => import( '../Facility/KioskEntry'));
const EquipmentList = lazyRetry(() => import( '../Facility/EquipmentList'));
const Equipment = lazyRetry(() => import( '../Facility/Equipment'));
const EquipmentLogs = lazyRetry(() => import( '../Facility/EquipmentLogList'));
const EquipmentLog = lazyRetry(() => import( '../Facility/EquipmentLog'));
const RoomDetails = lazyRetry(() => import( '../Facility/RoomDetails'));
const RoomSettings = lazyRetry(() => import( '../Facility/RoomSettings'));
const LocationList = lazyRetry(() => import( '../Facility/LocationList'));
const LocationDetail = lazyRetry(() => import( '../Facility/LocationDetail'));
const LocationInspection = lazyRetry(() => import( '../Facility/LocationInspection'));
const VendorList = lazyRetry(() => import( '../Inventory/Vendors'));
const Vendor = lazyRetry(() => import( '../Crm/Vendor'));
const VendorAuditList = lazyRetry(() => import( '../Investigations/VendorAuditList'));
const VendorAudit = lazyRetry(() => import( '../Investigations/VendorAudit'));
const CleaningSolutionList = lazyRetry(() => import( '../Facility/CleaningSolutions'));
const CleaningSolution = lazyRetry(() => import( '../Facility/CleaningSolution'));
const FeedingLotList = lazyRetry(() => import( '../Cultivation/FeedingLots'));
const FeedingLot = lazyRetry(() => import( '../Cultivation/FeedingLot'));
const CustomerList = lazyRetry(() => import( '../Crm/Customers'));
const Customer = lazyRetry(() => import( '../Crm/Customer'));
const LogList = lazyRetry(() => import( '../Admin/Logs'));
const ScheduledActivities = lazyRetry(() => import( '../Management/ScheduledActivities'));
const ScheduledActivity = lazyRetry(() => import( '../Management/ScheduledActivity'));
const RoomCheckList = lazyRetry(() => import( '../Facility/RoomChecks'));
const RoomCheck = lazyRetry(() => import( '../Facility/RoomCheck'));
const ItemCategories = lazyRetry(() => import( '../Inventory/ItemCategories'));
const ItemList = lazyRetry(() => import( '../Inventory/Items'));
const Item = lazyRetry(() => import( '../Inventory/Item'));
const Strains = lazyRetry(() => import( '../Cultivation/Strains'));
const CropTreatmentList = lazyRetry(() => import('../Cultivation/IpmTreatments'));
const CropTreatment = lazyRetry(() => import( '../Cultivation/CropTreatment'));
const TestItemCategories = lazyRetry(() => import( '../Quality/TestItemCategories'));
const TestItemList = lazyRetry(() => import( '../Quality/TestItemList'));
const TestDefinitionList = lazyRetry(() => import( '../Quality/TestDefinitionList'));
const TestDefinition = lazyRetry(() => import( '../Quality/TestDefinition'));
const TestResultList = lazyRetry(() => import( '../Quality/TestResultList'));
const TestResults = lazyRetry(() => import( '../Quality/TestResults'));
const LotList = lazyRetry(() => import( '../Inventory/Lots'));
const Lot = lazyRetry(() => import( '../Inventory/Lot'));
const PortalHome = lazyRetry(() => import('../Portal/PortalHome'));
const PortalLots = lazyRetry(() => import('../Portal/PortalLots'));
const PortalLot = lazyRetry(() => import('../Portal/PortalLot'));
const PortalProductionOrders = lazyRetry(() => import('../Portal/PortalProductionOrders'));
const PortalProductionOrder = lazyRetry(() => import('../Portal/PortalProductionOrder'));
const PortalProductionBatches = lazyRetry(() => import('../Portal/PortalProductionBatches'));
const PortalProductionBatch = lazyRetry(() => import('../Portal/PortalProductionBatch'));
const PortalReceipts = lazyRetry(() => import('../Portal/PortalReceipts'));
const PortalReceipt = lazyRetry(() => import('../Portal/PortalReceipt'));
const PortalRegister = lazyRetry(() => import('../Portal/PortalRegister'));
const PortalAccept = lazyRetry(() => import('../Portal/PortalAccept'));
const PortalEntry = lazyRetry(() => import('../Portal/PortalEntry'));
const BannerAdmin = lazyRetry(() => import('../Banners/BannerAdmin'));
const BannerAnalytics = lazyRetry(() => import('../Banners/BannerAnalytics'));
const PartnerUploadsInbox = lazyRetry(() => import('../Crm/PartnerUploadsInbox'));
const Rooms = lazyRetry(() => import( '../Facility/Rooms'));
const Room = lazyRetry(() => import('../Facility/Room'));
const Routings = lazyRetry(() => import('../Production/Routings'));
const Routing = lazyRetry(() => import('../Production/Routing'));
const ProductionBoms = lazyRetry(() => import('../Production/ProductionBoms'));
const ProductionBom = lazyRetry(() => import('../Production/ProductionBom'));
const CropTreatmentPrint = lazyRetry(() => import('../Cultivation/CropTreatmentPrint'));
const IncidentPrint = lazyRetry(() => import('../Investigations/IncidentPrint'));
const NcrPrint = lazyRetry(() => import('../Investigations/NcrPrint'));
const LossPrint = lazyRetry(() => import('../Investigations/LossPrint'));
// The Messages screen is the chat surface; `Message` is now only the legacy /message/:id
// redirect that resolves an old message id to its thread.
const MessageList = lazyRetry(() => import('../General/Messages/MessagesScreen'));
const Message = lazyRetry(() => import('../General/Messages/MessageRedirect'));
const UserProfile = lazyRetry(() => import('../General/UserProfile'));
const MaintenanceRequestList = lazyRetry(() => import('../Facility/MaintenanceRequests'));
const MaintenanceRequest = lazyRetry(() => import('../Facility/MaintenanceRequest'));
const TenantList = lazyRetry(() => import('../Config/TenantList'));
const Tenant = lazyRetry(() => import('../Config/Tenant'));
const HCReportList = lazyRetry(() => import('../Reports/HCReports'));
const HCReport = lazyRetry(() => import('../Reports/HCReport'));
const AnnualRevenueReportList = lazyRetry(() => import('../Reports/AnnualRevenueReports'));
const AnnualRevenueReport = lazyRetry(() => import('../Reports/AnnualRevenuReport'));
const SaskatchewanReportList = lazyRetry(() => import('../Reports/SaskatchewanReports'));
const QADashboard = lazyRetry(() => import('../Dashboards/QADashboard'));
const Strain = lazyRetry(() => import('../Cultivation/Strain'));
const CultivationDashboard = React.lazy(() =>  import('../Dashboards/CulitivationDashboard'));
const FileManager = lazyRetry(() => import('../Documents/FileManager'));
const ManagedDocumentList = lazyRetry(() => import('../Documents/ManagedDocuments'));
const ManagedDocument = lazyRetry(() => import('../Documents/ManagedDocument'));
const GroupTrainingSessions = lazyRetry(() => import('../Documents/GroupTraining/GroupTrainingSessions'));
const GroupTrainingSession = lazyRetry(() => import('../Documents/GroupTraining/GroupTrainingSession'));
const DocumentAnalyticsDashboard = lazyRetry(() => import('../Documents/DocumentAnalytics/AnalyticsDashboard'));
const DocumentReviewList = lazyRetry(() => import('../Documents/DocumentReviewList'));
const DocumentReview = lazyRetry(() => import('../Documents/DocumentReview'));
const ProductionPlan = lazyRetry(() => import('../Management/ProductionPlan'));
const ProductionPlanList = lazyRetry(() => import('../Management/ProductionPlanList'));
const ProductionScheduleList = lazyRetry(() => import('../Management/ProductionScheduleList'));
const ProductionSchedule = lazyRetry(() => import('../Management/ProductionSchedule'));
const ChangeRequestList = lazyRetry(() => import('../Documents/ChangeRequestList'));
const ChangeRequest = lazyRetry(() => import('../Documents/ChangeRequest'));
const ScreenDefinitions = lazyRetry(() => import('../Config/ScreenDefinitions'));
const Screens = lazyRetry(() => import('../Config/Screens'));
const Screen = lazyRetry(() => import('../Config/Screen'));
const Location = lazyRetry(() => import('../Facility/Location'));
const ProcessingDashboard = lazyRetry(() => import('../Dashboards/ProcessingDashboard'));
const InventoryDashboard = lazyRetry(() => import('../Dashboards/InventoryDashboard'));
const BatchTemplates = lazyRetry(() => import('../Cultivation/BatchTemplates'));
const BatchTemplate = lazyRetry(() => import('../Cultivation/BatchTemplate'));
const BatchActivityDefinitions = lazyRetry(() => import('../Cultivation/BatchActivityDefinitions'));
const CultivationBatchList = lazyRetry(() => import('../Cultivation/CultivationBatches'));
const CultivationBatch = lazyRetry(() => import('../Cultivation/CultivationBatch'));
const EvoraBatchPrint = lazyRetry(() => import('../Cultivation/EvoraBatchPrint'));
const BatchActivity = lazyRetry(() => import('../Cultivation/BatchActivity'));
const BinList = lazyRetry(() => import('../Facility/Bins'));
const Bin = lazyRetry(() => import('../Facility/Bin'));
const ContainerList = lazyRetry(() => import('../Inventory/ContainerList'));
const Container = lazyRetry(() => import('../Inventory/Container'));
const EProductionOrderList = lazyRetry(() => import('../Production/ProductionOrders'));
const EProductionOrder = lazyRetry(() => import('../Production/ProductionOrder'));
const ProductionOrderActivity = lazyRetry(() => import('../Production/ProductionOrderActivity'));
const CuringList = lazyRetry(() => import('../Production/Curings'));
const Curing = lazyRetry(() => import('../Production/Curing'));
const ExternalTreatmentList = lazyRetry(() => import('../Quality/ExternalTreatments'));
const ExternalTreatment = lazyRetry(() => import('../Quality/ExternalTreatment'));
const ItemLedgers = lazyRetry(() => import('../Inventory/ItemLedgers'));
const ItemLedger = lazyRetry(() => import('../Inventory/ItemLedger'));
const SaleList = lazyRetry(() => import('../Crm/Sales'));
const Sale = lazyRetry(() => import('../Crm/Sale'));
const ShipmentList = lazyRetry(() => import('../Inventory/Shipments'));
const Shipment = lazyRetry(() => import('../Inventory/Shipment'));
const DocumentCertificationList = lazyRetry(() => import('../Documents/DocumentCertifications'));
const DryingOrderList = lazyRetry(() => import('../Production/DryingOrders'));
const DryingOrder = lazyRetry(() => import('../Production/DryingOrder'));
const DryingActivity = lazyRetry(() => import('../Production/DryingActivity'));
const TrimmingOrderList = lazyRetry(() => import('../Production/TrimmingOrders'));
const TrimmingOrder = lazyRetry(() => import('../Production/TrimmingOrder'));
const TrimmingActivity = lazyRetry(() => import('../Production/TrimmingActivity'));
const TenantUsers = lazyRetry(() => import('../Admin/TenantUsers'));
const TenantUser = lazyRetry(() => import('../Admin/TenantUser'));
const CuringActivity = lazyRetry(() => import('../Production/CuringActivity'));
const ObjectList = lazyRetry(() => import('../Config/ObjectList'));
const ObjectScreen = lazyRetry(() => import('../Config/ObjectScreen'));
const GlobalObjectList = lazyRetry(() => import('../Config/GlobalObjectList'));
const GlobalObjectScreen = lazyRetry(() => import('../Config/GlobalObjectScreen'));
const EmailTemplateList = lazyRetry(() => import('../Admin/EmailTemplateList'));
const EmailTemplate = lazyRetry(() => import('../Admin/EmailTemplate'));
const HelpLinks = lazyRetry(() => import('../Admin/HelpLinks'));
const HelpLinkEntry = lazyRetry(() => import('../Admin/HelpLinkEntry'));
const GuideAdministration = lazyRetry(() => import('../Admin/Guides/GuideAdministration'));
const ResetPassword = lazyRetry(() => import('../ResetPassword'));
const EmailSentList = lazyRetry(() => import('../Admin/EmailSentList'));
const EmailSent = lazyRetry(() => import('../Admin/EmailSent'));
const RoomCleaningList = lazyRetry(() => import('../Facility/RoomCleaningList'));
const RoomCleaning = lazyRetry(() => import('../Facility/RoomCleaning'));
const HeadersAndFooters = lazyRetry(() => import('../Admin/HeadersAndFooters'));
const BinSummary = lazyRetry(() => import('../Inventory/BinSummary'));
const HeadersFootersList = lazyRetry(() => import('../Admin/HeadersFootersList'));
const TenantReportList = lazyRetry(() => import('../Admin/TenantReportList'));
const TenantReport = lazyRetry(() => import('../Admin/TenantReport'));
const Management = lazyRetry(() => import('../Config/Management'));
const ReceiptList = lazyRetry(() => import('../Inventory/Receipts'));
const Receipt = lazyRetry(() => import('../Inventory/Receipt'));
const PurchaseOrderList = lazyRetry(() => import('../Inventory/PurchaseOrders'));
const PurchaseOrder = lazyRetry(() => import('../Inventory/PurchaseOrder'));
const Countries = lazyRetry(() => import('../Config/Countries'));
const Provinces = lazyRetry(() => import('../Config/Provinces'));
const DestructionReport = lazyRetry(() => import('../Inventory/DestructionReport'));
const ProductionBatches = lazyRetry(() => import('../Production/ProductionBatches'));
const ProductionBatch = lazyRetry(() => import('../Production/ProductionBatch'));
const ProductionBatchPrint = lazyRetry(() => import('../Production/ProductionBatchPrint'));
const ManageUsers = lazyRetry(() => import('../HrManagement/ManageUsers'));
const ManageUser = lazyRetry(() => import('../HrManagement/ManageUser'));
const CustomerComplaints = lazyRetry(() => import('../Crm/CustomerComplaints'));
const CustomerComplaint = lazyRetry(() => import('../Crm/CustomerComplaint'));
const ManagedDocumentTemplates = lazyRetry(() => import('../Documents/ManagedDocumentTemplates'));
const ManagedDocumentTemplate = lazyRetry(() => import('../Documents/ManagedDocumentTemplate'));
const Cars = lazyRetry(() => import('../Investigations/Cars'));
const Car = lazyRetry(() => import('../Investigations/Car'));
const ApiLogs = lazyRetry(() => import('../Admin/ApiLogs'));
const DataUpload = lazyRetry(() => import('../Config/DataUpload'));
const DataUploadList = lazyRetry(() => import('../Config/DataUploadList'));
const LaunchPad = lazyRetry(() => import('../LaunchPad/LaunchPad'));
const CannabisInventory = lazyRetry(() => import('../Inventory/CannabisInventory'));
const RecallList = lazyRetry(() => import('../Investigations/RecallList'));
const Recall = lazyRetry(() => import('../Investigations/Recall'));
const InventoryBreakdown = lazyRetry(() => import('../Inventory/InventoryBreakdown'));
const PrintView = lazyRetry(() => import('../Printing/PrintView'));
const AnnouncementList = lazyRetry(() => import('../Admin/AnnouncementList'));
const Announcement = lazyRetry(() => import('../Admin/Announcement'));
const RoomFeedingList = lazyRetry(() => import('../Cultivation/RoomFeedings'));
const RoomFeeding = lazyRetry(() => import('../Cultivation/RoomFeeding'));
const FeedingRecipeList = lazyRetry(() => import('../Cultivation/FeedingRecipes'));
const FeedingRecipe = lazyRetry(() => import('../Cultivation/FeedingRecipe'));
const PestScoutingList = lazyRetry(() => import('../Cultivation/PestScoutings'));
const PestScouting = lazyRetry(() => import('../Cultivation/PestScouting'));
const CcxPostingList = lazyRetry(() => import('../Crm/CcxPostingList'));
const CcxPosting = lazyRetry(() => import('../Crm/CcxPosting'));
const MarketMap = lazyRetry(() => import('../Crm/MarketMap'));
const ExciseAdjustmentList = lazyRetry(() => import('../Inventory/ExciseAdjustmentList'));
const UacReport = lazyRetry(() => import('../Admin/Printing/UACReport'));
const ComplianceEvidence = lazyRetry(() => import('../Admin/ComplianceEvidence'));
const PesticideRecipes = lazyRetry(() => import('../Cultivation/PesticideRecipes'));
const PesticideRecipe = lazyRetry(() => import('../Cultivation/PesticideRecipe'));
const EquipmentCalibrationChecks = lazyRetry(() => import('../Facility/EquipmentCalibrationChecks'));
const EquipmentCalibrationCheck = lazyRetry(() => import('../Facility/EquipmentCalibrationCheck'));
const EquipmentCalibrationTypes = lazyRetry(() => import('../Facility/EquipmentCalibrationTypes'));
const EquipmentCalibrationType = lazyRetry(() => import('../Facility/EquipmentCalibrationType'));
const InvoiceList = lazyRetry(() => import('../Crm/Invoices'));
const Invoice = lazyRetry(() => import('../Crm/Invoice'));
const AvailableInventory = lazyRetry(() => import('../Reports/AvailableInventory'));
const AvailableInventoryList = lazyRetry(() => import('../Reports/AvailableInventoryList'));
const ExciseStampAudit = lazyRetry(() => import('../Reports/ExciseStampAudit'));
const MasterCaseList = lazyRetry(() => import('../Production/MasterCases'));
const MasterCase = lazyRetry(() => import('../Production/MasterCase'));
const CapaList = lazyRetry(() => import('../Investigations/CapaActions'));
const BatchActivities = lazyRetry(() => import('../Cultivation/BatchActivities'));
const CultivationHub = lazyRetry(() => import('../Cultivation/CultivationHub'));
const ProcessingHub = lazyRetry(() => import('../Production/ProcessingHub'));
const ProductionFloorHub = lazyRetry(() => import('../Production/ProductionFloorHub'));
const PlannerHub = lazyRetry(() => import('../Production/PlannerHub'));
const SalesHub = lazyRetry(() => import('../Crm/SalesHub'));
const CleaningSolutionRecipes = lazyRetry(() => import('../Facility/CleaningSolutionRecipes'));
const CleaningSolutionRecipe = lazyRetry(() => import('../Facility/CleaningSolutionRecipe'));
const RiskAssessments = lazyRetry(() => import('../Quality/RiskAssessments'));
const RiskAssessment = lazyRetry(() => import('../Quality/RiskAssessment'));
const PastInventoryList = lazyRetry(() => import('../Inventory/PastInventoryList'));
const PastInventory = lazyRetry(() => import('../Inventory/PastInventory'));
const QueueJobs = lazyRetry(() => import('../Config/QueueJobs'));
const QueueJob = lazyRetry(() => import('../Config/QueueJob'));
const SalesDashboard = lazyRetry(() => import('../Dashboards/SalesDashboard'));
const Login = React.lazy (() => import('../Login'));
const Gtins = lazyRetry(() => import('../Inventory/Gtins'));
const Gtin = lazyRetry(() => import('../Inventory/Gtin'));
const OperationsDashboard = lazyRetry(() => import('../Dashboards/OperationsDashboard'));
const DashboardContainer = lazyRetry(() => import('../Dashboard/DashboardContainer'));
const SavedQueries = lazyRetry(() => import('../Dashboard/SavedQueries'));
const SavedQuery = lazyRetry(() => import('../Dashboard/SavedQuery'));
const DashboardAdmin = lazyRetry(() => import('../Dashboard/DashboardAdmin'));
const AdminLogs = lazyRetry(() => import('../Config/Logs'));
const AdminLanding = lazyRetry(() => import('../AdminLanding'));
const ProductionTotals = lazyRetry(() => import('../Reports/ProductionTotals'));
const ProductionTotal = lazyRetry(() => import('../Reports/ProductionTotal'));
const ConsignmentSales = lazyRetry(() => import('../Crm/ConsignmentSales'));
const ContractSales = lazyRetry(() => import('../Crm/ContractSales'));
const ContractSale = lazyRetry(() => import('../Crm/Dialogs/ContractSale'));
const DispensaryList = lazyRetry(() => import('../Admin/DispensaryList'));
const Dispensary = lazyRetry(() => import('../Admin/Dispensary'));
const Teams = lazyRetry(() => import('../Facility/Teams'));
const Team = lazyRetry(() => import('../Facility/Team'));
const Performance = lazyRetry(() => import('../Reports/Performance'));
const HealthDashboard = lazyRetry(() => import('../Admin/HealthDashboard'));
const LocationAudits = lazyRetry(() => import('../Facility/LocationAudits'));
const LocationAudit = lazyRetry(() => import('../Facility/LocationAudit'));
const SearchResults = lazyRetry(() => import('../Search/SearchResults'));

export { ConsignmentSales };
export { ContractSales };
export { ContractSale };
export { DispensaryList };
export { Dispensary };
export { Teams };
export { Team };
export { Login };
export { Home };
export { SopList };
export { Sop };
export { FormTemplates };
export { FormRecord };
export { UserList };
export { User };
export { DocumentCertification };
export { AdminSettings };
export { Deviation };
export { DeviationList };
export { NonConformance };
export { NcrList };
export { ChangePassword };
export { FormComponent };
export { Recall };
export { RecallList };
export { DestructionList };
export { Destruction };
export { ValueTypes };
export { Values };
export { IncidentList };
export { Incident };
export { EmployeeIllnessList };
export { EmployeeIllness };
export { LossCannabisList };
export { LossCannabis };
export { PesticidePreparationList };
export { PesticidePreparation };
export { VistorList };
export { Visitor };
export { SigninList };
export { Signin };
export { EquipmentList };
export { Equipment };
export { EquipmentLogs };
export { EquipmentLog };
export { RoomDetails };
export { RoomSettings };
export { LocationList };
export { LocationDetail };
export { LocationInspection };
export { VendorList };
export { Vendor };
export { VendorAuditList };
export { VendorAudit };
export { CleaningSolutionList };
export { CleaningSolution };
export { FeedingLotList };
export { FeedingLot };
export { CustomerList };
export { Customer };
export { LogList };
export { ScheduledActivities };
export { ScheduledActivity };
export { RoomCheckList };
export { RoomCheck };
export { ItemCategories };
export { ItemList };
export { Item };
export { Strains };
export { CropTreatmentList };
export { CropTreatment };
export { TestItemCategories };
export { TestItemList };
export { TestDefinitionList };
export { TestDefinition };
export { TestResultList };
export { TestResults };
export { LotList };
export { Lot };
export { PortalHome };
export { PortalLots };
export { PortalLot };
export { PortalProductionOrders };
export { PortalProductionOrder };
export { PortalProductionBatches };
export { PortalProductionBatch };
export { PortalReceipts };
export { PortalReceipt };
export { PortalRegister };
export { PortalAccept };
export { PortalEntry };
export { BannerAdmin };
export { BannerAnalytics };
export { PartnerUploadsInbox };
export { Rooms };
export { Room };
export { Routings };
export { Routing };
export { ProductionBoms };
export { ProductionBom };
export { CropTreatmentPrint };
export { IncidentPrint };
export { NcrPrint };
export { LossPrint };
export { MessageList };
export { Message };
export { UserProfile };
export { MaintenanceRequestList };
export { MaintenanceRequest };
export { TenantList };
export { Tenant };
export { HCReportList };
export { HCReport };
export { AnnualRevenueReportList };
export { AnnualRevenueReport };
export { SaskatchewanReportList };
export { QADashboard };
export { Strain };
export { CultivationDashboard };
export { FileManager };
export { ManagedDocumentList};
export { ManagedDocument };
export { GroupTrainingSessions };
export { GroupTrainingSession };
export { DocumentAnalyticsDashboard };
export { DocumentReviewList };
export { DocumentReview };
export { ProductionPlan };
export { ProductionPlanList };
export { ProductionScheduleList };
export { ProductionSchedule };
export { ChangeRequestList };
export { ChangeRequest };
export { ScreenDefinitions };
export { Screens };
export { Screen };
export { Location };
export { ProcessingDashboard };
export { BatchTemplates };
export { BatchTemplate };
export { BatchActivityDefinitions};
export { CultivationBatchList };
export { CultivationBatch };
export { EvoraBatchPrint };
export { BatchActivity };
export { BinList };
export { Bin };
export { ContainerList };
export { Container };
export { EProductionOrderList };
export { EProductionOrder };
export { ProductionOrderActivity };
export { CuringList };
export { Curing };
export { ExternalTreatmentList };
export { ExternalTreatment };
export { ItemLedgers };
export { ItemLedger };
export { SaleList };
export { Sale };
export { ShipmentList };
export { Shipment };
export { DocumentCertificationList };
export { DryingOrderList };
export { DryingOrder };
export { DryingActivity };
export { TrimmingOrderList };
export { TrimmingOrder };
export { TrimmingActivity };
export { TenantUsers };
export { TenantUser };
export { CuringActivity };
export { ObjectList };
export { ObjectScreen };
export { GlobalObjectList };
export { GlobalObjectScreen };
export { EmailTemplateList };
export { EmailTemplate };
export { ResetPassword };
export { EmailSentList };
export { EmailSent };
export { RoomCleaningList };
export { RoomCleaning };
export { HeadersAndFooters };
export { BinSummary };
export { HeadersFootersList };
export { TenantReportList };
export { TenantReport };
export { Management };
export { ReceiptList };
export { Receipt };
export { PurchaseOrderList };
export { PurchaseOrder };
export { Countries };
export { Provinces };
export { DestructionReport };
export { ProductionBatches };
export { ProductionBatch };
export { ProductionBatchPrint };
export { ManageUsers };
export { ManageUser };
export { CustomerComplaints };
export { CustomerComplaint };
export { ManagedDocumentTemplates };
export { ManagedDocumentTemplate };
export { Cars };
export { Car };
export { ApiLogs };
export { DataUpload };
export { DataUploadList };
export { LaunchPad };
export { CannabisInventory };
export { InventoryBreakdown };
export { PrintView };
export { AnnouncementList };
export { Announcement };
export { RoomFeedingList };
export { RoomFeeding };
export { FeedingRecipe };
export { FeedingRecipeList };
export { PestScouting };
export { PestScoutingList };
export { CcxPostingList };
export { CcxPosting };
export { ExciseAdjustmentList };
export { UacReport };
export { ComplianceEvidence };
export { PesticideRecipes};
export { PesticideRecipe };
export { EquipmentCalibrationChecks };
export { EquipmentCalibrationCheck };
export { EquipmentCalibrationTypes };
export { EquipmentCalibrationType };
export { InvoiceList };
export { Invoice };
export { AvailableInventory };
export { AvailableInventoryList };
export { ExciseStampAudit };
export { MasterCaseList };
export { MasterCase };
export { CapaList };
export { BatchActivities };
export { CultivationHub };
export { ProcessingHub };
export { ProductionFloorHub };
export { PlannerHub };
export { SalesHub };
export { CleaningSolutionRecipes };
export { CleaningSolutionRecipe };
export { RiskAssessments };
export { RiskAssessment };
export { PastInventoryList };
export { PastInventory };
export { InventoryDashboard };
export { QueueJobs };
export { QueueJob };
export { SalesDashboard };
export { Gtins };
export { Gtin };
export { OperationsDashboard };
export { DashboardContainer };
export { SavedQueries };
export { SavedQuery };
export { DashboardAdmin };
export { AdminLogs };
export { AdminLanding };
export { ProductionTotals };
export { ProductionTotal };
export { SigninKiosk };
export { KioskEntry };
export { Performance };
export { HealthDashboard };
export { HelpLinks };
export { HelpLinkEntry };
export { LocationAudits };
export { LocationAudit };
export { GuideAdministration };
export { MarketMap };
export { SearchResults };
